import { privateApiClient } from './apiClient';
import { PAYMENT_REQUEST_VALIDITY_MINUTES } from '../config/environment';
import { normalizeAmountForApi } from '../utils/amount';
import { normalizePosMethod, methodPhone, resolveOperator } from '../utils/operator';

/**
 * Service pour gérer les demandes de paiement.
 * Payload aligné sur le backend (PaymentRequestRqsDTO) et le mobile (AddPaymentRequestScreen).
 */

/**
 * Construit le payload attendu par le backend (même structure que le mobile).
 * @param {Object} input - Données fournies par l'UI (Admin/Caissier)
 * @returns {Object} Payload conforme à PaymentRequestRqsDTO
 */
function buildPaymentRequestPayload(input) {
    const amount = normalizeAmountForApi(input.amount, input.currency);
    const terminalId = input.terminal_id ?? input.terminal ?? null;

    // paymentMethod : TransactionMethodRqsPayload (backend attend expiration_date, pas expiry_date)
    const rawPm = input.paymentMethod ?? {};
    const paymentMethod = {
        id: rawPm.id ?? null,
        provider_id: rawPm.provider_id ?? null,
        provider_name: rawPm.provider_name ?? null,
        prefix: rawPm.prefix ?? null,
        account: rawPm.account ?? null,
        suffix: rawPm.suffix ?? null,
        full_name: rawPm.full_name ?? null,
        expiration_date: rawPm.expiration_date ?? rawPm.expiry_date ?? null,
        control: rawPm.control ?? null,
        owner: rawPm.owner ?? null,
    };

    const client = input.client ?? input.customer ?? null;

    // Backend POS (PaymentRequestRqsModel) : champ « terminal » = id du PaymentTerminal (comme le mobile).
    return {
        terminal: terminalId,
        company_id: input.company_id ?? null,
        article_id: input.article_id ?? null,
        amount: amount ?? 0,
        currency: input.currency ?? 'CDF',
        code: input.code ?? null,
        title: input.title ?? 'Paiement Flash',
        minAmount: input.minAmount ?? 0,
        maxAmount: input.maxAmount ?? 0,
        type: input.type ?? 'flash',
        with_custom_title: input.with_custom_title ?? !!input.title,
        set_limits: input.set_limits ?? false,
        can_set_limits: input.can_set_limits ?? false,
        limit_usage: input.limit_usage ?? false,
        max_usage: input.max_usage ?? 1,
        collect_customer_name: input.collect_customer_name ?? false,
        collect_customer_phone: input.collect_customer_phone ?? false,
        payment_target: input.payment_target ?? 'produit',
        description: input.description ?? input.title ?? '',
        button_action: input.button_action ?? 'payer',
        collect_customer_address: input.collect_customer_address ?? false,
        collect_customer_email: input.collect_customer_email ?? false,
        active_advanced_options: input.active_advanced_options ?? false,
        suggested_amount: input.suggested_amount ?? true,
        send_invoice: input.send_invoice ?? false,
        finish_message: input.finish_message ?? 'Merci pour votre fidélité',
        client,
        paymentMethod,
    };
}

/**
 * Créer une nouvelle demande de paiement (même API que le mobile : POST /pos/payment-requests).
 * @param {Object} paymentRequest - Données de la demande (title, amount, currency, company_id, terminal_id ou terminal, paymentMethod, customer, etc.)
 * @returns {Promise<Object>} { error, message, payload, request_id?, fees?, netAmount? }
 */
/** ID à utiliser pour le suivi WebSocket / polling après création. */
export const extractPaymentRequestId = (response) => {
    if (!response) return null;
    const payload = response.payload ?? response;
    return (
        response.request_id ??
        payload?.request_id ??
        payload?.id ??
        null
    );
};

export const isPaymentRequestSettled = (status) => {
    const s = String(status || '').toLowerCase();
    return s === 'paid' || s === 'cashed' || s === 'collected' || s === 'completed' || s === 'approved';
};

/** Statut depuis GET /pos/payment-requests/{id} (DTO direct ou enveloppe). */
export const resolvePaymentRequestStatus = (details) => {
    if (!details || typeof details !== 'object') return null;
    if (details.status != null) return details.status;
    if (details.payload?.status != null) return details.payload.status;
    return null;
};

export const createPaymentRequest = async (paymentRequest) => {
    try {
        const normalizedAmount = normalizeAmountForApi(paymentRequest.amount, paymentRequest.currency);
        if (normalizedAmount == null || normalizedAmount <= 0) {
            throw new Error('Montant invalide');
        }

        const payload = buildPaymentRequestPayload({
            ...paymentRequest,
            amount: normalizedAmount,
        });

        if (!payload.terminal) {
            throw new Error('Veuillez sélectionner un terminal');
        }
        if (!payload.company_id) {
            throw new Error('Organisation non sélectionnée');
        }

        const response = await privateApiClient.post('/pos/payment-requests', payload);
        return response.data;
    } catch (error) {
        if (error.response) {
            const data = error.response.data;
            const msg =
                data?.message
                || (typeof data?.error === 'string' ? data.error : null)
                || data?.target
                || (error.response.status === 500
                    ? 'Erreur serveur (POS/transaction). Vérifiez le numéro, le réseau et réessayez.'
                    : null);
            throw new Error(msg || 'Erreur lors de la création de la demande de paiement');
        }
        throw error;
    }
};

/**
 * Récupérer la liste des demandes de paiement
 * @param {Object} params - Paramètres de filtrage
 * @param {string} params.sts - Statut (all, pending, paid, cashed, expired)
 * @param {string} params.org - ID de l'organisation
 * @param {string} params.emt - ID de l'employé (ou "all" pour admin)
 * @param {string} params.cmr - ID du client (ou "all")
 * @param {string} params.tag - Tag du terminal (ou "all")
 * @param {string} params.slg - Slug de recherche
 * @param {number} params.pge - Numéro de page
 * @param {number} params.lmt - Limite par page
 * @returns {Promise<Object>} La liste des demandes de paiement
 */
/**
 * Normalise une demande (PaymentRequestRspDTO du pos-service) → format UI (admin + caissier).
 * Le payeur vient du client de la demande, sinon du moyen de paiement de sa dernière transaction.
 */
export const mapPaymentRequestFromApi = (req, { cashierNameById = {} } = {}) => {
    if (!req) return null;
    const createDate = req.createAt || req.create_at || req.createdAt || new Date();
    const dateObj = new Date(createDate);
    const lastTx = Array.isArray(req.transactions) && req.transactions.length
        ? req.transactions[req.transactions.length - 1]
        : null;
    const method = lastTx?.method ? normalizePosMethod(lastTx.method) : null;
    const payerPhone = methodPhone(method)
        || (req.client?.phoneNumber ? `${req.client.zip ? `+${req.client.zip} ` : ''}${req.client.phoneNumber}` : '');
    const clientName = req.client?.name && req.client.name !== 'Anonyme' ? req.client.name : null;
    const expiresAt = new Date(dateObj.getTime() + PAYMENT_REQUEST_VALIDITY_MINUTES * 60 * 1000);
    let status = String(req.status || 'pending').toLowerCase();
    if (status === 'pending' && expiresAt < new Date()) status = 'expired';

    return {
        id: req.id,
        code: req.code,
        title: req.title || 'Demande de paiement',
        amount: req.amount || 0,
        currency: req.currency || 'CDF',
        caissier: cashierNameById[req.emitter] || (req.emitter ? 'Caissier' : '—'),
        client: clientName || payerPhone || 'Client',
        phone: payerPhone,
        terminal: req.terminal?.tag ? `Terminal ${req.terminal.tag}` : '—',
        status,
        scans: 0,
        createdAt: dateObj,
        expiresAt,
        paidAt: req.paid_at ? new Date(req.paid_at) : null,
        // Référence commune : code de transaction du transaction-service (TXN-…-PMT…)
        reference: lastTx?.trans_code || lastTx?.code || req.code || req.id,
        description: req.title || 'Demande de paiement',
        method: req.type || 'flash',
        paymentMethod: method,
        operator: resolveOperator({ name: method?.providerName, providerId: method?.providerId, phone: payerPhone }),
        raw: req,
    };
};

/** Statuts connus du pos-service (PaymentRequestStatus) ; « expirée » est calculé côté client. */
const API_PAYMENT_REQUEST_STATUSES = ['draft', 'pending', 'paid', 'cashed', 'failed'];

export const fetchPaymentRequests = async (params = {}) => {
    try {
        // L'API exige tous ces paramètres (sinon 400 et liste vide), dont `typ` (flash / link / all)
        const query = {
            pge: 1,
            lmt: 10,
            typ: 'all',
            tag: 'all',
            sts: 'all',
            emt: 'all',
            cmr: 'all',
            slg: '',
            ...params,
        };
        const statuses = String(query.sts || 'all').split(',').filter((s) => API_PAYMENT_REQUEST_STATUSES.includes(s));
        query.sts = statuses.length ? statuses.join(',') : 'all';
        const response = await privateApiClient.get('/pos/payment-requests', { params: query });
        console.log('[paymentRequestService] Réponse:', {
            total: response.data?.contents?.length || 0,
            sample: response.data?.contents?.[0] || null
        });
        return response.data;
    } catch (error) {
        console.error('[paymentRequestService] Erreur lors de la récupération:', error);
        if (error.response) {
            throw new Error(error.response.data?.message || error.response.data?.error || 'Erreur lors de la récupération des demandes');
        }
        throw error;
    }
};

/**
 * Récupérer les détails d'une demande de paiement
 * @param {string} requestId - ID de la demande
 * @returns {Promise<Object>} Les détails de la demande
 */
export const fetchPaymentRequestDetails = async (requestId) => {
    try {
        console.log('[paymentRequestService] Récupération des détails pour:', requestId);
        const response = await privateApiClient.get(`/pos/payment-requests/${requestId}`);
        return response.data;
    } catch (error) {
        console.error('[paymentRequestService] Erreur lors de la récupération des détails:', error);
        if (error.response) {
            throw new Error(error.response.data?.message || error.response.data?.error || 'Erreur lors de la récupération des détails');
        }
        throw error;
    }
};
